import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

import { LoadingStatus } from '../../../../../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../../../../../data/patient/domain/patient';
import { RemovePatientFromGroupModalFacade } from './remove-patient-from-group-modal.facade';

export interface RemovePatientFromGroupModalResponse {
  groupId: string;
  target: Patient[];
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-remove-patient-from-group-modal',
  templateUrl: './remove-patient-from-group-modal.component.html',
  styleUrls: ['./remove-patient-from-group-modal.component.scss'],
  providers: [RemovePatientFromGroupModalFacade],
})
export class RemovePatientFromGroupModalComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() target: Patient[] | Patient;
  @Output() validate = new EventEmitter<RemovePatientFromGroupModalResponse>();
  @Output() cancel = new EventEmitter<void>();
  public isLoading = false;
  public isRemoving = false;
  public error?: string;
  public selectedGroupId: string;
  private groups: PatientGroup[];
  private dietitianStateSub: Subscription;
  private dietitian?: Dietitian;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: RemovePatientFromGroupModalFacade,
    private modalService: BsModalService,
    private router: Router,
  ) {
    // Nada
  }

  ngAfterViewInit(): void {
    // Nada
  }

  ngOnInit(): void {
    this.dietitianStateSub = this.facade.dietitianState$.subscribe((state) => {
      this.dietitian = state.entity;

      if (state.loading == LoadingStatus.COMPLETE) {
        this.loadPatientGroups().then();
      }
    });
  }

  ngOnDestroy(): void {
    this.dietitianStateSub?.unsubscribe();
  }

  selectGroup(id: string) {
    this.selectedGroupId = id;
  }

  hideModal() {
    this.cancel.emit();
    this.bsModalRef.hide();
  }

  filterGroups(): PatientGroup[] {
    if (this.groups) {
      return this.groups.filter((g) => this.alreadyGrouped(g.id.toString()));
    } else {
      return [];
    }
  }

  targetName(): string {
    if (Array.isArray(this.target)) {
      if (this.target.length > 1) {
        return ' les ' + this.target.length + ' patients';
      } else {
        return ' le patient ' + this.target[0].fullName;
      }
    } else {
      return ' le patient ' + this.target.fullName;
    }
  }

  public async loadPatientGroups() {
    this.isLoading = true;
    this.error = undefined;
    try {
      if (this.dietitian != null) {
        this.groups = await this.facade.getDietitianPatientGroups(
          this.dietitian?.id.toString(),
        );
      } else {
        this.groups = [];
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  alreadyGrouped(id: string) {
    if (Array.isArray(this.target)) {
      return (
        this.target.map((t) => t.groupId).filter((g) => g && g.indexOf(id) >= 0)
          .length === this.target.length
      );
    } else {
      return this.target.groupId && this.target.groupId.indexOf(id) >= 0;
    }
  }

  async removePatientFromGroup() {
    if (!this.selectedGroupId || !this.dietitian) {
      return;
    }
    if (this.dietitian) {
      this.updateTarget(this.target, this.selectedGroupId, this.dietitian);
    }
  }

  private updateTarget(
    target: Patient[] | Patient,
    selectedGroupId: string,
    dietitian: Dietitian,
  ) {
    this.isRemoving = true;
    if (Array.isArray(target)) {
      this.facade
        .removeAllPatientsFromGroup(
          dietitian.id.toString(),
          target.map((t) => t.id.toString()),
          selectedGroupId,
        )
        .then((patients) => {
          this.validate.emit({
            groupId: this.selectedGroupId,
            target: patients,
          });
          this.hideModal();
        })
        .finally(() => {
          this.isRemoving = false;
        });
    } else {
      this.facade
        .removePatientFromGroup(
          dietitian.id.toString(),
          target.id.toString(),
          selectedGroupId,
        )
        .then(() => {
          this.validate.emit({
            groupId: this.selectedGroupId,
            target: [this.target as Patient],
          });
          this.hideModal();
        })
        .finally(() => {
          this.isRemoving = false;
        });
    }
  }

  multiTarget() {
    return Array.isArray(this.target);
  }
}
